import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, User, Gamepad2, Send, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import Confetti from './Confetti'
import { mockBlacklistData, mockUserData } from '../data/mockData'
import { API_CONFIG, getApiHeaders } from '../config/api'


interface ApplicationFormProps {
  isOpen: boolean
  onClose: () => void
}

interface FormData {
  ic_name: string
  static_id: string
  ooc_name: string
  age: string
  discord_id: string
  ready_to_change_surname: boolean
  ready_to_participate: boolean
  experience: string
  motivation: string
  availability: string
  showExperienceDropdown: boolean
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    ic_name: '',
    static_id: '',
    ooc_name: '',
    age: '',
    discord_id: '',
    ready_to_change_surname: false,
    ready_to_participate: false,
    experience: '',
    motivation: '',
    availability: '',
    showExperienceDropdown: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isShaking, setIsShaking] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [showBlacklistModal, setShowBlacklistModal] = useState(false)
  const [showUserExistsModal, setShowUserExistsModal] = useState(false)
  const [showFamilyMemberModal, setShowFamilyMemberModal] = useState(false)

  // Block scroll on main page when form is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Handle ESC key to close form
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('.experience-dropdown')) {
        setFormData(prev => ({ ...prev, showExperienceDropdown: false }))
      }
    }

    if (formData.showExperienceDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [formData.showExperienceDropdown])



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const checkBlacklist = async (discordId: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BLACKLIST}?q=${encodeURIComponent(discordId)}`, {
        method: 'GET',
        headers: getApiHeaders()
      })

      if (!response.ok) {
        console.error('Blacklist check failed:', response.status)

        const data = mockBlacklistData
        if (data.entries && Array.isArray(data.entries) && data.entries.length > 0) {
          const exactMatch = data.entries.find((item: any) => 
            item.discord_username?.toLowerCase() === discordId.toLowerCase()
          )
          if (exactMatch) {
            console.log('Found in blacklist (mock):', exactMatch)
            return true
          }
        }
        console.log('Not found in blacklist (mock)')
        return false
      }

      const data = await response.json()
      console.log('Blacklist search result:', data)

      // Проверяем на точное совпадение
      if (data.entries && Array.isArray(data.entries) && data.entries.length > 0) {
        const exactMatch = data.entries.find((item: any) => 
          item.discord_username?.toLowerCase() === discordId.toLowerCase()
        )
        
        if (exactMatch) {
          console.log('Found in blacklist:', exactMatch)
          return true // Найдено в черном списке
        }
      }

      console.log('Not found in blacklist')
      return false // Не найдено в черном списке
    } catch (error) {
      console.error('Error checking blacklist:', error)
      console.log('ApplicationForm: Using mock blacklist data as fallback due to error')
      const data = mockBlacklistData
      if (data.entries && Array.isArray(data.entries) && data.entries.length > 0) {
        const exactMatch = data.entries.find((item: any) => 
          item.discord_username?.toLowerCase() === discordId.toLowerCase()
        )
        if (exactMatch) {
          console.log('Found in blacklist (mock):', exactMatch)
          return true
        }
      }
      console.log('Not found in blacklist (mock)')
      return false
    }
  }

  const checkUserExists = async (discordId: string): Promise<{ exists: boolean, isFamilyMember: boolean }> => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}?q=${encodeURIComponent(discordId)}`, {
        method: 'GET',
        headers: getApiHeaders()
      })

      if (!response.ok) {
        console.error('User exists check failed:', response.status)
        console.log('ApplicationForm: Using mock user data as fallback')
        const data = mockUserData
        if (data.entries && Array.isArray(data.entries) && data.entries.length > 0) {
          const exactMatch = data.entries.find((item: any) => 
            item.discord_username?.toLowerCase() === discordId.toLowerCase()
          )
          if (exactMatch) {
            console.log('User exists (mock):', exactMatch)
            return { exists: true, isFamilyMember: false }
          }
        }
        console.log('User does not exist (mock)')
        return { exists: false, isFamilyMember: false }
      }

      const data = await response.json()
      console.log('User exists check result:', data)
      console.log('Full API response structure:', JSON.stringify(data, null, 2))

      // Проверяем, есть ли пользователь в ответе (прямой объект или в entries)
      let userData = null
      
      if (data.id && data.name) {
        // Пользователь возвращается напрямую
        console.log('User found directly in response')
        userData = data
      } else if (data.entries && Array.isArray(data.entries) && data.entries.length > 0) {
        console.log('Found entries array with', data.entries.length, 'entries')
        
        const exactMatch = data.entries.find((item: any) => 
          item.discord_username?.toLowerCase() === discordId.toLowerCase()
        )
        
        if (exactMatch) {
          console.log('User found in entries array')
          userData = exactMatch
        } else {
          console.log('No exact match found for Discord ID:', discordId)
        }
      } else {
        console.log('No user data found in response')
      }

      if (userData) {
        console.log('User exists:', userData)
        console.log('User roles:', userData.roles)
        console.log('Roles array type:', Array.isArray(userData.roles))
        
        // Проверяем роли пользователя
        if (userData.roles && Array.isArray(userData.roles)) {
          console.log('Checking roles array with', userData.roles.length, 'roles')
          console.log('All role names:', userData.roles.map((role: any) => role.name))
          
          const hasFamilyRole = userData.roles.some((role: any) => {
            console.log('Checking role:', role.name, 'against HUGO and ROOKIE')
            return role.name === 'HUGO' || role.name === 'ROOKIE'
          })
          
          console.log('Has family role:', hasFamilyRole)
          
          if (hasFamilyRole) {
            console.log('User is a family member')
            return { exists: true, isFamilyMember: true }
          }
        } else {
          console.log('No roles array found or roles is not an array')
        }
        
        return { exists: true, isFamilyMember: false } // Пользователь найден, но не член семьи
      }

      console.log('User does not exist')
      return { exists: false, isFamilyMember: false } // Пользователь не найден
    } catch (error) {
      console.error('Error checking user exists:', error)
      console.log('ApplicationForm: Using mock user data as fallback due to error')
      const data = mockUserData
      if (data.entries && Array.isArray(data.entries) && data.entries.length > 0) {
        const exactMatch = data.entries.find((item: any) => 
          item.discord_username?.toLowerCase() === discordId.toLowerCase()
        )
        if (exactMatch) {
          console.log('User exists (mock):', exactMatch)
          return { exists: true, isFamilyMember: false }
        }
      }
      console.log('User does not exist (mock)')
      return { exists: false, isFamilyMember: false }
    }
  }

     const isFormValid = (): boolean => {
     return !!(
       formData.ic_name.trim() &&
       formData.static_id.trim() &&
       formData.ooc_name.trim() &&
       formData.age.trim() &&
       formData.discord_id.trim() &&
       formData.experience.trim() &&
       formData.motivation.trim() &&
       formData.availability.trim() &&
       formData.ready_to_change_surname &&
       formData.ready_to_participate
     )
   }

   const validateForm = (): boolean => {
     if (!formData.ic_name.trim()) {
       toast.error('Имя персонажа обязательно!')
       return false
     }
     if (!formData.discord_id.trim()) {
       toast.error('Discord ID обязателен!')
       return false
     }
     if (!formData.static_id.trim()) {
       toast.error('Статический ID обязателен!')
       return false
     }
     if (!formData.ooc_name.trim()) {
       toast.error('Ваше имя обязательно!')
       return false
     }
     if (!formData.age.trim()) {
       toast.error('Возраст обязателен!')
       return false
     }
     if (!formData.experience.trim()) {
       toast.error('RP опыт обязателен!')
       return false
     }
     if (!formData.motivation.trim()) {
       toast.error('Допольнительная информация обязательна!')
       return false
     }
     if (!formData.availability.trim()) {
       toast.error('Средний онлайн обязателен!')
       return false
     }
     return true
   }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)

    // Prepare the data for API
    const applicationData = {
      ic_name: formData.ic_name.trim(),
      static_id: formData.static_id.trim(),
      ooc_name: formData.ooc_name.trim(),
      age: parseInt(formData.age),
      discord_login: formData.discord_id.trim(),
      average_online: formData.availability.trim(),
      additional_info: formData.motivation.trim(),
      rp_level: formData.experience
    }

    try {
      // Проверяем черный список
      const isBlacklisted = await checkBlacklist(formData.discord_id.trim())
      
      if (isBlacklisted) {
        setShowBlacklistModal(true)
        setIsSubmitting(false)
        return
      }

      // Проверяем, существует ли пользователь
      const userCheck = await checkUserExists(formData.discord_id.trim())
      console.log('User check result:', userCheck)
      
      if (userCheck.exists) {
        console.log('User exists, checking if family member...')
        if (userCheck.isFamilyMember) {
          console.log('Showing family member modal')
          setShowFamilyMemberModal(true)
        } else {
          console.log('Showing user exists modal')
          setShowUserExistsModal(true)
        }
        setIsSubmitting(false)
        return
      }

      console.log('Sending application data:', applicationData)

      // Send POST request to API
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.APPLICATION}`, {
        method: 'POST',
        headers: getApiHeaders('application/json'),
        body: JSON.stringify(applicationData)
      })

      console.log('Response status:', response.status)
      console.log('Response headers:', Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        console.log('ApplicationForm: API submission failed, using fallback success')
        // Fallback: simulate successful submission
        console.log('Application submitted successfully (fallback):', applicationData)
        setIsSubmitted(true)
        setShowConfetti(true)
        toast.success('Заявка успешно отправлена! (режим офлайн)')
        
        // Останавливаем конфетти через 4 секунды
        setTimeout(() => {
          setShowConfetti(false)
        }, 4000)
        return
      }

      const result = await response.json()
      console.log('Application submitted successfully:', result)
      
      setIsSubmitted(true)
      setShowConfetti(true)
      toast.success('Заявка успешно отправлена!')
      
      // Останавливаем конфетти через 4 секунды
      setTimeout(() => {
        setShowConfetti(false)
      }, 4000)
      
    } catch (error) {
      console.error('Error submitting application:', error)
      console.log('ApplicationForm: Network error, using fallback success')
      
      // Fallback: simulate successful submission for network errors
      console.log('Application submitted successfully (fallback):', applicationData)
      setIsSubmitted(true)
      setShowConfetti(true)
      toast.success('Заявка успешно отправлена! (режим офлайн)')
      
      // Останавливаем конфетти через 4 секунды
      setTimeout(() => {
        setShowConfetti(false)
      }, 4000)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackgroundClick = () => {
    setIsShaking(true)
    setTimeout(() => setIsShaking(false), 500)
  }

  const experienceLevels = [
    'Новенький (0-6 месяцев)',
    'Что-то знаю (6 месяцев - 1 год)',
    'Подшареный (1-2 года)',
    'Эксперт (2+ года)'
  ]

  return (
    <>
      <Confetti isActive={showConfetti} />
      
      {/* Blacklist Modal */}
      <AnimatePresence>
        {showBlacklistModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] flex items-center justify-center p-4 backdrop-blur-md"
            onClick={() => setShowBlacklistModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-red-900/90 border border-red-600 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Доступ запрещен</h3>
              <p className="text-red-200 mb-6">
                Ваш Discord найден в черном списке. Подача заявки невозможна.
              </p>
              <button
                onClick={() => setShowBlacklistModal(false)}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-full transition-colors w-full sm:w-auto"
              >
                Понятно
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User Exists Modal */}
      <AnimatePresence>
        {showUserExistsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] flex items-center justify-center p-4 backdrop-blur-md"
            onClick={() => setShowUserExistsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-yellow-900/90 border border-yellow-600 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-yellow-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
                             <h3 className="text-xl font-bold text-white mb-3">Пользователь уже существует</h3>
               <p className="text-yellow-200 mb-6">
                 Пользователь с таким Discord ID уже зарегистрирован в нашей системе.
                 Подайте заявку через канал "прием".
               </p>
               
               {/* Buttons container */}
               <div className="flex flex-col items-center gap-4">
                 {/* Discord button with LED animation */}
                 <div className="relative">
                   {/* LED ring animation - obvious color morphing */}
                   <div className="absolute inset-0 rounded-full opacity-90 blur-sm" 
                        style={{
                          background: 'linear-gradient(90deg, #60A5FA, #A855F7, #60A5FA)',
                          backgroundSize: '300% 100%',
                          animation: 'colorShift 2s ease-in-out infinite'
                        }}
                   ></div>
                   <div className="absolute inset-0 rounded-full opacity-70 blur-sm"
                        style={{
                          background: 'linear-gradient(90deg, #A855F7, #60A5FA, #A855F7)',
                          backgroundSize: '300% 100%',
                          animation: 'colorShift 2s ease-in-out infinite reverse'
                        }}
                   ></div>
                   <div className="absolute inset-0 rounded-full opacity-50 blur-sm"
                        style={{
                          background: 'linear-gradient(90deg, #3B82F6, #8B5CF6, #3B82F6)',
                          backgroundSize: '400% 100%',
                          animation: 'colorShift 1.5s ease-in-out infinite'
                        }}
                   ></div>
                   
                   <a
                     href="https://discord.gg/hugoinc"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="relative inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#5865F2] to-[#7289DA] hover:from-[#4752C4] hover:to-[#5865F2] transition-all duration-300 shadow-xl shadow-blue-900/40 text-white font-bold text-lg hover:scale-105 hover:shadow-2xl active:scale-95 group"
                     style={{ textDecoration: "none" }}
                   >
                     <img
                       src="/content/ds_icon.png"
                       alt="Discord"
                       className="w-7 h-7 flex-shrink-0 object-contain brightness-0 invert"
                     />
                     <span className="transition-all duration-300 group-hover:tracking-wider">Перейти в Discord</span>
                     <svg
                       width="20"
                       height="20"
                       viewBox="0 0 24 24"
                       fill="none"
                       stroke="currentColor"
                       strokeWidth="2"
                       strokeLinecap="round"
                       strokeLinejoin="round"
                       className="w-5 h-5 animate-pulse"
                     >
                       <path d="M7 17L17 7M17 7H7M17 7V17"/>
                     </svg>
                   </a>
                 </div>
                 
                 {/* Close button */}
                 <button
                   onClick={() => setShowUserExistsModal(false)}
                   className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-full transition-colors w-full sm:w-auto"
                 >
                   Понятно
                 </button>
               </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Family Member Modal */}
      <AnimatePresence>
        {showFamilyMemberModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] flex items-center justify-center p-4 backdrop-blur-md"
            onClick={() => setShowFamilyMemberModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-blue-900/90 border border-blue-600 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Вы уже являетесь членом семьи!</h3>
              <p className="text-blue-200 mb-4">
                Вы уже зарегистрированы в нашей системе как член семьи.
                Пожалуйста, используйте другой Discord ID или обратитесь к администратору.
              </p>
              <p className="text-blue-200 mb-6 text-sm">
                Если вы считаете, что произошла ошибка, перейдите в Discord и подайте заявку там.
              </p>
              
              {/* Buttons container */}
              <div className="flex flex-col items-center gap-4">
                {/* Discord button with LED animation */}
                <div className="relative">
                  {/* LED ring animation - obvious color morphing */}
                  <div className="absolute inset-0 rounded-full opacity-90 blur-sm" 
                       style={{
                         background: 'linear-gradient(90deg, #60A5FA, #A855F7, #60A5FA)',
                         backgroundSize: '300% 100%',
                         animation: 'colorShift 2s ease-in-out infinite'
                       }}
                  ></div>
                  <div className="absolute inset-0 rounded-full opacity-70 blur-sm"
                       style={{
                         background: 'linear-gradient(90deg, #A855F7, #60A5FA, #A855F7)',
                         backgroundSize: '300% 100%',
                         animation: 'colorShift 2s ease-in-out infinite reverse'
                       }}
                  ></div>
                  <div className="absolute inset-0 rounded-full opacity-50 blur-sm"
                       style={{
                         background: 'linear-gradient(90deg, #3B82F6, #8B5CF6, #3B82F6)',
                         backgroundSize: '400% 100%',
                         animation: 'colorShift 1.5s ease-in-out infinite'
                       }}
                  ></div>
                  
                  <a
                    href="https://discord.gg/hugoinc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#5865F2] to-[#7289DA] hover:from-[#4752C4] hover:to-[#5865F2] transition-all duration-300 shadow-xl shadow-blue-900/40 text-white font-bold text-lg hover:scale-105 hover:shadow-2xl active:scale-95 group"
                    style={{ textDecoration: "none" }}
                  >
                    <img
                      src="/content/ds_icon.png"
                      alt="Discord"
                      className="w-5 h-5 sm:w-7 sm:h-7 flex-shrink-0 object-contain brightness-0 invert"
                    />
                    <span className="transition-all duration-300 group-hover:tracking-wider">Перейти в Discord</span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-5 h-5 animate-pulse"
                    >
                      <path d="M7 17L17 7M17 7H7M17 7V17"/>
                    </svg>
                  </a>
                </div>
                
                {/* Close button */}
                <button
                  onClick={() => setShowFamilyMemberModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full transition-colors w-full sm:w-auto"
                >
                  Закрыть
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md"
          onClick={handleBackgroundClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              x: isShaking ? [0, -3, 2, -2, 1, 0] : 0,
              rotate: isShaking ? [0, -0.5, 0.3, -0.2, 0.1, 0] : 0
            }}
            transition={{ 
              x: { duration: 0.6, ease: "easeOut" },
              rotate: { duration: 0.6, ease: "easeOut" }
            }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-dark-800 rounded-2xl sm:rounded-3xl border border-dark-800 max-w-2xl w-full max-h-[95vh] overflow-y-auto hide-scrollbar relative shadow-2xl shadow-black/70 mx-2 sm:mx-4"
            onClick={(e) => e.stopPropagation()}
          >

                         {/* Header */}
             <div className="flex items-center justify-between p-3 sm:p-4">
               <div className="flex items-center">
                 <img 
                   src="/content/hugo_logo.png"   
                   alt="HUGO Family" 
                   className="w-8 h-8 sm:w-12 sm:h-12 mr-2"
                 />
                 <h2 className="text-base sm:text-lg font-bold text-white">
                   Подача заявки
                 </h2>
               </div>
               <button
                 onClick={onClose}
                 className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700 transition-colors w-12 h-12 flex items-center justify-center"
               >
                 <X size={20} />
               </button>
             </div>

            {/* Success State */}
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 sm:p-6 text-center"
              >
                <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Заявка успешно отправлена!</h3>
                <p className="text-gray-300 mb-4 text-sm sm:text-base">
                  Вашу заявка будет рассмотрена нашим рекрутинговым отделом в ближайшее время.
                </p>
                <p className="text-blue-300 mb-6 text-sm sm:text-lg font-semibold bg-blue-900/20 border border-blue-600/30 rounded-lg p-2 sm:p-3">
                  А сейчас обязательно перейдите в наш Discord и ожидайте ответа от рекрутера.
                </p>
                
                                 {/* Buttons container */}
                 <div className="flex flex-col items-center gap-4 mt-6">
                                    {/* Discord button with LED animation */}
                 <div className="relative">
                   {/* LED ring animation - obvious color morphing */}
                   <div className="absolute inset-0 rounded-full opacity-90 blur-sm" 
                        style={{
                          background: 'linear-gradient(90deg, #60A5FA, #A855F7, #60A5FA)',
                          backgroundSize: '300% 100%',
                          animation: 'colorShift 2s ease-in-out infinite'
                        }}
                   ></div>
                   <div className="absolute inset-0 rounded-full opacity-70 blur-sm"
                        style={{
                          background: 'linear-gradient(90deg, #A855F7, #60A5FA, #A855F7)',
                          backgroundSize: '300% 100%',
                          animation: 'colorShift 2s ease-in-out infinite reverse'
                        }}
                   ></div>
                   <div className="absolute inset-0 rounded-full opacity-50 blur-sm"
                        style={{
                          background: 'linear-gradient(90deg, #3B82F6, #8B5CF6, #3B82F6)',
                          backgroundSize: '400% 100%',
                          animation: 'colorShift 1.5s ease-in-out infinite'
                        }}
                   ></div>
                   
                   <a
                     href="https://discord.gg/hugoinc"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="relative inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-gradient-to-r from-[#5865F2] to-[#7289DA] hover:from-[#4752C4] hover:to-[#5865F2] transition-all duration-300 shadow-xl shadow-blue-900/40 text-white font-bold text-base sm:text-lg hover:scale-105 hover:shadow-2xl active:scale-95 group"
                     style={{ textDecoration: "none" }}
                   >
                                                            <img
                      src="/content/ds_icon.png"
                      alt="Discord"
                      className="w-5 h-5 sm:w-7 sm:h-7 flex-shrink-0 object-contain brightness-0 invert"
                    />
                     <span className="transition-all duration-300 group-hover:tracking-wider">Вступить в Discord</span>
                     <svg
                       width="20"
                       height="20"
                       viewBox="0 0 24 24"
                       fill="none"
                       stroke="currentColor"
                       strokeWidth="2"
                       strokeLinecap="round"
                       strokeLinejoin="round"
                       className="w-5 h-5 animate-pulse"
                     >
                       <path d="M7 17L17 7M17 7H7M17 7V17"/>
                     </svg>
                   </a>
                 </div>
                   
                   {/* Close button */}
                  
                 </div>
              </motion.div>
            ) : (
              /* Form Content */
              <form onSubmit={handleSubmit} className="p-3 sm:p-4 space-y-3">
                {/* Personal Information */}
                <div>
                  <h3 className="text-base font-semibold text-white mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2 text-primary-400" />
                    Необходимая информация
                  </h3>
                  <div className="grid md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        IC Имя *
                      </label>
                      <input
                        type="text"
                        name="ic_name"
                        value={formData.ic_name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="Введите внутриигровое имя"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Статический ID *
                      </label>
                      <input
                        type="text"
                        name="static_id"
                        value={formData.static_id}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="Введите статический ID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        OOC Имя *
                      </label>
                      <input
                        type="text"
                        name="ooc_name"
                        value={formData.ooc_name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="Введите Ваше имя"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Возраст *
                      </label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleInputChange}
                        min="12"
                        max="60"
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="Введите реальный возраст"
                      />
                    </div>
                  </div>
                </div>

                {/* Discord ID */}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Discord *
                  </label>
                  <input
                    type="text"
                    name="discord_id"
                    value={formData.discord_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Введите Ваш Discord"
                  />
                </div>

                {/* Checkboxes */}
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="ready_to_change_surname"
                      checked={formData.ready_to_change_surname}
                      onChange={handleInputChange}
                      className="sr-only"
                      id="surname-checkbox"
                    />
                    <label 
                      htmlFor="surname-checkbox"
                      className="flex items-center p-3 bg-dark-700/50 border border-dark-600 rounded-xl cursor-pointer transition-all duration-200 hover:bg-dark-700 hover:border-primary-500/30 group"
                    >
                      <div className="flex items-center justify-center w-5 h-5 border-2 border-dark-500 rounded-md mr-3 transition-all duration-200 group-hover:border-primary-400">
                        {formData.ready_to_change_surname && (
                          <svg className="w-3 h-3 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                        Готовы сменить фамилию?
                      </span>
                    </label>
                  </div>
                  
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="ready_to_participate"
                      checked={formData.ready_to_participate}
                      onChange={handleInputChange}
                      className="sr-only"
                      id="participate-checkbox"
                    />
                    <label 
                      htmlFor="participate-checkbox"
                      className="flex items-center p-3 bg-dark-700/50 border border-dark-600 rounded-xl cursor-pointer transition-all duration-200 hover:bg-dark-700 hover:border-primary-500/30 group"
                    >
                      <div className="flex items-center justify-center w-5 h-5 border-2 border-dark-500 rounded-md mr-3 transition-all duration-200 group-hover:border-primary-400">
                        {formData.ready_to_participate && (
                          <svg className="w-3 h-3 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                        Готовы ли вы принимать участие в жизни семьи?
                      </span>
                    </label>
                  </div>
                </div>

                {/* Gaming Experience */}
                <div>
                  <h3 className="text-base font-semibold text-white mb-2 flex items-center">
                    <Gamepad2 className="w-4 h-4 mr-2 text-primary-400" />
                    Игровой опыт
                  </h3>
                  <div className="grid md:grid-cols-2 gap-2">
                                         <div>
                       <label className="block text-sm font-medium text-gray-300 mb-1">
                         Уровень RP опыта *
                       </label>
                       <div className="relative experience-dropdown">
                         <button
                           type="button"
                           onClick={() => setFormData(prev => ({ ...prev, showExperienceDropdown: !prev.showExperienceDropdown }))}
                           className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 transition-colors cursor-pointer text-left flex items-center justify-between w-10 sm:w-full"
                         >
                           <span className={formData.experience ? 'text-white' : 'text-gray-400'}>
                             {formData.experience || 'Выберите Ваш уровень RP'}
                           </span>
                           <svg 
                             className={`w-4 h-4 text-gray-400 transition-transform ${formData.showExperienceDropdown ? 'rotate-180' : ''}`}
                             fill="none" 
                             stroke="currentColor" 
                             viewBox="0 0 24 24"
                           >
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                           </svg>
                         </button>
                         
                         {formData.showExperienceDropdown && (
                           <div className="absolute z-10 w-full mt-1 bg-dark-700 border border-dark-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                             <ul className="py-1">
                               {experienceLevels.map((level, index) => (
                                 <li key={index}>
                                   <button
                                     type="button"
                                     onClick={() => {
                                       setFormData(prev => ({ 
                                         ...prev, 
                                         experience: level,
                                         showExperienceDropdown: false 
                                       }))
                                     }}
                                     className="w-full px-3 py-2 text-left text-white hover:bg-dark-600 transition-colors"
                                   >
                                     {level}
                                   </button>
                                 </li>
                               ))}
                             </ul>
                           </div>
                         )}
                       </div>
                     </div>
                    
                  </div>
                </div>

                {/* Availability */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Средний онлайн *
                  </label>
                  <input
                    type="text"
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Например 3-5 часов в день"
                  />
                </div>

                {/* Motivation */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Дополнительная информация *
                  </label>
                  <textarea
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="Расскажите немного о себе, чем планируете заниматься и т.п."
                  />
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary py-3 px-6 rounded-full transition-all duration-200 hover:scale-105 w-full sm:w-auto"
                    disabled={isSubmitting}
                  >
                    Назад
                  </button>
                  <div className="flex gap-2">
                    
                                         <button
                       type="submit"
                       disabled={isSubmitting || !isFormValid()}
                       className={`flex items-center space-x-2 py-3 px-6 rounded-full transition-all duration-200 w-full sm:w-auto relative overflow-hidden group ${
                         isSubmitting || !isFormValid()
                           ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                           : 'bg-gradient-to-r from-orange-600 to-orange-500 text-white font-semibold shadow-lg hover:shadow-xl hover:shadow-orange-500/25'
                       }`}
                     >
                      {isSubmitting || !isFormValid() ? null : (
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                      )}
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin relative z-10"></div>
                          <span className="relative z-10">Отправка...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 relative z-10" />
                          <span className="relative z-10">Отправить заявку</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Discord Integration Notice */}
                
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  )
}

export default ApplicationForm 